import React, { useContext, useMemo, useCallback } from 'react';
import _ from 'underscore';
import classNames from 'classnames';
import { Stack } from '@fluentui/react';
import { Collapse, ValidatedFixedPanel, ValidatedPanel, collapseExpander, useCollapseState, getPanelErrorProps } from '@bingads-webui-campaign/wizard-jumbo-card';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withScope } from '@bingads-webui-react/scope-service';
import { useFormService } from '@bingads-webui-react/form';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { ValidationGroup } from '@bingads-webui-react/validation';
import { FormErrorGroup, UnmappedFormErrors, andComp, entityIs, propertyIs } from '@bingads-webui-campaign-react/form-error';
import { withControlErrorHandling, withUnvalidatedControlErrors } from '@bingads-webui-campaign-react/error-handling';
import { ValidatedLegacyDateSetting } from '@bingads-webui-campaign/date-setting';
import { AdGroup } from '@bingads-webui/campaign-odata-schemas';
import { RemainingCharsValidatedTextField } from '@bingads-webui-campaign-react/remaining-chars-hint';
import { ERROR_ENTITY } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { AudienceBidValue, SectionShimmer, bidValueIcon, startDateEndDateIcon } from '@bingads-webui-campaign-react/display-plus-campaigns';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ValidatedUrlOptionsParameter } from '@bingads-webui-campaign-react/validated-url-input';
import { UrlOptionsImg } from '@bingads-webui-campaign-react/perf-max-step-wizard';

import { getAdGroupSettingsPanels, getAdGroupStyles } from './utils';
import { ValidatedLegacyAdDistribution } from './components/ad-distribution';
import { ValidatedAdRotation } from './components';
import { isSearchShoppingCampaign, isAutoBidding } from '../step-utils';
import { TextTitle } from '../../shared/pannel-header';

const ServerValidatedAdDistributionSetting = withControlErrorHandling(withUnvalidatedControlErrors(
  ValidatedLegacyAdDistribution,
  {
    filter: andComp(entityIs(ERROR_ENTITY.AdGroup), propertyIs('adDistribution')),
  }
));

const AdGroupSettingsInternal = ({
  data,
  onDataChange,
  error,
  flowType,
}) => {
  const i18n = useContext(I18nContext);
  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [ValidationStringMap]), [i18n]);
  const classes = useDefaultStyles(getAdGroupStyles);
  const { state: { context: { saveFailed: showAllValidationErrors } } } = useFormService();
  const serverContext = useContext(ServerContext);

  const {
    adGroup,
  } = data || {};
  const { CeilingValue, FloorValue } = serverContext.ProductGroupBidRange || {};
  const bidStrategyType = _.get(data, ['campaign', 'BiddingScheme', 'Type'], '');
  const showBid = !isAutoBidding(bidStrategyType);

  const { basicOptions } = getAdGroupSettingsPanels(flowType, showBid);
  const [adDistributionView, setAdDistributionView] = React.useState(null);
  const [
    allAdGroupSettingsSections,
    adGroupSettingsExpandedSections,
    setAdGroupSettingsExpandedSections,
  ] = useCollapseState(basicOptions, basicOptions); // all expanded by default
  const allBasicExpanded = useMemo(
    () => collapseExpander({ setActiveKeys: setAdGroupSettingsExpandedSections }),
    [setAdGroupSettingsExpandedSections]
  );
  const isSearchShopping = isSearchShoppingCampaign(flowType);

  const onValidate = useCallback((result) => {
    if (data && data.adGroup) {
      onDataChange({
        data: {
          ...data,
          adGroup: {
            ...data.adGroup,
            ...result.data,
            SearchBid: data.adGroup.SearchBid,
            CpmBid: data.adGroup.CpmBid,
          },
        },
        isValid: result.isValid,
      });
    }
  }, [onDataChange, data]);

  return (
    <div className={classNames(classes.adgroupShell, 'adgroup-shell', 'adgroup-step')}>
      <ActuationGroup actuationProps={{ isActive: false }}>
        <AttentionBeaconGroup>
          <FormErrorGroup formErrorProps={{ errors: error || [], showAllValidationErrors }}>
            <ValidationGroup
              validationProps={{
                schema: AdGroup,
                data: (data && data.adGroup) || {},
                i18n: legacyI18n,
                onValidate,
              }}
            >
              <div className="server-errors">
                <UnmappedFormErrors />
              </div>
              <Stack className="base-options-stack" horizontal tokens={{ childrenGap: 14 }}>
                <Collapse allPanelItems={allAdGroupSettingsSections} activeKeys={adGroupSettingsExpandedSections} setActiveKeys={setAdGroupSettingsExpandedSections} checkItems skipItemsTypeCheck>
                  <ValidatedFixedPanel
                    key="ad-group-name"
                    title={<TextTitle titleKey={_TL_('Ad group name')} topicKey="pop_BA_CampaignCreate_AudTargets_AdGroupName" />}
                    attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                  >
                    {
                      adGroup ? (
                        <RemainingCharsValidatedTextField
                          placeholder={_TL_('Enter a name for your ad group')}
                          id="input-ad-group-name"
                          validationProps={{
                            field: 'Name',
                            schema: AdGroup,
                          }}
                          i18n={i18n}
                          formErrorProps={{ filter: andComp(entityIs(ERROR_ENTITY.AdGroup), propertyIs('Name')) }}
                        />
                      ) : <SectionShimmer count={3} />
                    }
                  </ValidatedFixedPanel>
                  <ValidatedPanel
                    key="bid-value"
                    title={<TextTitle titleKey={_TL_('Default bid')} icon={bidValueIcon} topicKey="pop_BA_CampaignCreate_Budget_DefaultBid" />}
                    attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                  >
                    {
                      adGroup ? (
                        <AudienceBidValue
                          data={data}
                          onDataChange={onDataChange}
                          legacyI18n={legacyI18n}
                          ceilingValue={CeilingValue}
                          floorValue={FloorValue}
                        />
                      ) : (<SectionShimmer count={3} />)
                    }
                  </ValidatedPanel>
                  <ValidatedPanel
                    key="url-options"
                    title={<TextTitle titleKey={_TL_('Ad group URL options')} icon={UrlOptionsImg} />}
                    attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                    actuationProps={{
                      expand: allBasicExpanded,
                      expandKey: 'url-options',
                    }}
                  >
                    <div className={classes.urlOptionsContainer}>
                      <span className="url-options-descriptions">
                        {i18n.getString(_TL_('Set up the tracking and redirect information for your ads.'))}
                      </span>
                      {
                        adGroup ?
                          <ValidatedUrlOptionsParameter
                            i18n={i18n}
                            validationProps={{
                              initialValue: adGroup,
                              data: adGroup,
                            }}
                          /> : <SectionShimmer count={3} />
                      }
                    </div>
                  </ValidatedPanel>
                  <ValidatedPanel
                    key="start-end-dates"
                    title={<TextTitle
                      titleKey={_TL_('Start and end dates')}
                      icon={startDateEndDateIcon}
                    />}
                    attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                  >
                    <ValidatedLegacyDateSetting
                      i18n={i18n}
                      validationProps={{
                        data: {
                          StartDate: data && data.adGroup && data.adGroup.StartDate,
                          EndDate: data && data.adGroup && data.adGroup.EndDate,
                          TimeZone: data && data.campaign && data.campaign.Timezone,
                          isCreate: data && data.adGroup && !data.adGroup.Id,
                        },
                      }}
                    />
                  </ValidatedPanel>
                  {
                    isSearchShopping && (
                      <ValidatedPanel
                        key="ad-distribution"
                        title={<TextTitle
                          titleKey={_TL_('Ad distribution')}
                          details={adDistributionView ? adDistributionView.getTranslatedOption() : ''}
                        />}
                      >
                        <ServerValidatedAdDistributionSetting
                          setAdDistributionView={setAdDistributionView}
                        />
                      </ValidatedPanel>
                    )
                  }
                  <ValidatedPanel
                    key="ad-rotation"
                    title={<TextTitle titleKey={_TL_('Ad rotation')} />}
                  >
                    <ValidatedAdRotation
                      validationProps={{
                        data: adGroup,
                        schema: AdGroup,
                      }}
                    />
                  </ValidatedPanel>
                </Collapse>
              </Stack>
            </ValidationGroup>
          </FormErrorGroup>
        </AttentionBeaconGroup>
      </ActuationGroup>
    </div>
  );
};

export const AdGroupSettings = withScope(AdGroupSettingsInternal);
